<template>
    <div>
        <v-text-field-filled
            label="Objet du mail"
            v-model="mail.header"
            outlined
            ref="headerTextarea"
            density="compact"
            class="pb-4"
        >
            <template v-slot:append-inner>
                <MailOptions
                    v-model:text="mail.header"
                    reference="headerTextarea"
                    modelPropertyName="header"
                    :startPos="getTextareaCursorSelection(headerTextarea!).selectionStart"
                    :endPos="getTextareaCursorSelection(headerTextarea!).selectionEnd"
                    :isInvoiceNextDueDate="true"
                    @OnMailOptionSelected="loadNewCursorHeaderPos"
                />
            </template>
        </v-text-field-filled>

        <div class="py-2 editor__container">
            <label for="upperBody" class="editor__container__label">Corps du mail supérieur</label>
            <div class="d-flex align-stretch">
                <Editor
                    v-model="mail.upperBody"
                    ref="upperBodyTextarea"
                    id="upperBody"
                    class="flex-grow-1"
                />
                <div class="d-flex flex-column editor__container__options">
                    <MailOptions
                        v-model:text="mail.upperBody"
                        reference="upperBodyTextarea"
                        modelPropertyName="upperBody"
                        @OnMailOptionSelected="loadNewCursorUpperBodyPos"
                    />
                    <div>
                        <v-btn-icon
                            icon="$fa-redo-alt"
                            size="small"
                            @click="mail.upperBody = ''"
                            v-tooltip:bottom="'Remettre les valeurs par défauts'"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="py-2 mt-4 editor__container">
            <label for="lineBody" class="editor__container__label">Ligne du corps du mail</label>
            <div class="d-flex align-stretch">
                <Editor
                    v-model="mail.lineBody"
                    ref="lineBodyTextarea"
                    id="lineBody"
                    class="flex-grow-1"
                />
                <div class="d-flex flex-column editor__container__options">
                    <MailOptions
                        v-model:text="mail.lineBody"
                        reference="lineBodyTextarea"
                        modelPropertyName="lineBody"
                        @OnMailOptionSelected="loadNewCursorLineBodyPos"
                    />
                    <div>
                        <v-btn-icon
                            icon="$fa-redo-alt"
                            size="small"
                            @click="mail.lineBody = ''"
                            v-tooltip:bottom="'Remettre les valeurs par défauts'"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="py-2 mt-4 editor__container">
            <label for="lowerBody" class="editor__container__label">Corps du mail inférieur</label>
            <div class="d-flex align-stretch">
                <Editor
                    v-model="mail.lowerBody"
                    ref="lowerBodyTextarea"
                    id="lowerBody"
                    class="flex-grow-1"
                />
                <div class="d-flex flex-column editor__container__options">
                    <MailOptions
                        v-model:text="mail.lowerBody"
                        reference="lowerBodyTextarea"
                        modelPropertyName="lowerBody"
                        @OnMailOptionSelected="loadNewCursorLowerBodyPos"
                    />
                    <div>
                        <v-btn-icon
                            icon="$fa-redo-alt"
                            size="small"
                            @click="mail.lowerBody = ''"
                            v-tooltip:bottom="'Remettre les valeurs par défauts'"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defaultMultipleMail } from "@/types/invoiceAdvanceSettings.types";
import MailOptions from "./MailOptions.vue";
import Editor from "@/components/editorMenu/Editor.vue";
import { Component, Vue, toNative, Ref } from "vue-facing-decorator";
import { Model } from "vue-facing-decorator";
import { MailEditorMixin, MailOptionData, TipTapEditor } from "@/components/mixins/mailEditorMixin";

@Component({
    components: {
        MailOptions,
        Editor,
    },
})
export class MultiLineMailEditor extends MailEditorMixin {
    @Model({ default: null }) readonly mail: defaultMultipleMail;

    @Ref() headerTextarea!: HTMLInputElement;
    @Ref() upperBodyTextarea!: { editor: TipTapEditor };
    @Ref() lineBodyTextarea!: { editor: TipTapEditor };
    @Ref() lowerBodyTextarea!: { editor: TipTapEditor };

    mounted() {
        // Formatage initial du contenu avant de l'injecter dans les éditeurs
        if (this.mail.upperBody) {
            this.mail.upperBody = this.formatTextToHTML(this.mail.upperBody);
        }
        if (this.mail.lineBody) {
            this.mail.lineBody = this.formatTextToHTML(this.mail.lineBody);
        }
        if (this.mail.lowerBody) {
            this.mail.lowerBody = this.formatTextToHTML(this.mail.lowerBody);
        }
    }

    loadNewCursorHeaderPos(data: any) {
        this.mail.header =
            this.headerTextarea.value.substring(
                0,
                this.getTextareaCursorSelection(this.headerTextarea!).selectionStart,
            ) +
            data.text +
            this.headerTextarea.value.substring(
                this.getTextareaCursorSelection(this.headerTextarea!).selectionEnd,
            );
        this.headerTextarea.selectionStart = data.newCursorPos;
        this.headerTextarea.selectionEnd = this.headerTextarea.selectionStart;
        this.headerTextarea.focus();
    }

    loadNewCursorUpperBodyPos(data: MailOptionData) {
        this.updateEditorContentWithMailOption(data, this.upperBodyTextarea, "mail.upperBody");
    }

    loadNewCursorLineBodyPos(data: MailOptionData) {
        this.updateEditorContentWithMailOption(data, this.lineBodyTextarea, "mail.lineBody");
    }

    loadNewCursorLowerBodyPos(data: MailOptionData) {
        this.updateEditorContentWithMailOption(data, this.lowerBodyTextarea, "mail.lowerBody");
    }
}

export default toNative(MultiLineMailEditor);
</script>

<style lang="scss" scoped>
.editor {
    &__container {
        background-color: #ececec;
        border-radius: 12px;
        &__label {
            color: black;
            font-size: 12.5px;
            margin-left: 1rem;
            opacity: 0.6;
        }
        &__options {
            margin: 0 13.5px;
            flex-shrink: 0;
            width: 28px;
        }
    }
}
</style>
